import React, { useMemo, useState } from 'react';

import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { Box, Button, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';

import TenantBrandChip from '~/components/Common/Elements/TenantBrandChip';
import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM } from '~/consts/roles';

import useCurrentUser from '~/hooks/useCurrentUser';

import { formatDateWeekDayShortMonth } from '~/services/TimeService';

import isProd from '~/utils/isProd';

import SpoofUserDialog from '../Common/SpoofUserDialog';

const PROD_TEST_USERS = [
  { id: 'e78297cc-0b82-466d-9453-86ea9074f053', name: 'test.account@luxgroup.com' },
  { id: '162be8f1-b4eb-4a6f-a581-8f164b684fd9', name: 'Test Account Lux Plus' },
];

const RECENTLY_VIEWED_USERS_STORAGE_KEY = 'recentlyViewedUsers';
const PINNED_USERS_STORAGE_KEY = 'pinnedRecentlyViewedUsers';

interface RecentUser {
  id: string;
  label: string;
  date: string;
  tenant?: string;
}

const isNewDay = (idx: number, date: string, recentUsers: Array<RecentUser>) => {
  if (idx === 0) {
    return true;
  }
  const previousDate = new Date(recentUsers[idx - 1].date);
  const currentDate = new Date(date);
  return previousDate.toDateString() !== currentDate.toDateString();
};

const isRecentUser = (user: Partial<RecentUser>): user is RecentUser => {
  return !!user?.id && !!user.label && !!user.date;
};

const getStoredUsers = (storageKey: string): Array<RecentUser> => {
  const savedUsers = localStorage.getItem(storageKey);

  if (!savedUsers) {
    return [];
  }

  try {
    const parsedUsers = JSON.parse(savedUsers);
    return Array.isArray(parsedUsers) ? parsedUsers.filter(isRecentUser) : [];
  } catch {
    return [];
  }
};

export default function RecentUsers() {
  const [pinnedUsers, setPinnedUsers] = useState<Array<RecentUser>>(() => getStoredUsers(PINNED_USERS_STORAGE_KEY));
  const recentUsers = useMemo(() => getStoredUsers(RECENTLY_VIEWED_USERS_STORAGE_KEY), []);
  const { user } = useCurrentUser();
  const [spoofUser, setSpoofUser] = useState<{ id: string; label: string; orderUuid?: string } | null>(null);
  const isProduction = isProd();
  const autoPinnedUserIds = [
    user?.id_member,
    ...(isProduction ? PROD_TEST_USERS.map((testUser) => testUser.id) : []),
  ].filter(Boolean);
  const pinnedUserIds = new Set([...autoPinnedUserIds, ...pinnedUsers.map((pinnedUser) => pinnedUser.id)]);
  const unpinnedRecentUsers = recentUsers.filter((recentUser) => !pinnedUserIds.has(recentUser.id));

  const pinUser = (recentUser: RecentUser) => {
    const updatedPinnedUsers = [recentUser, ...pinnedUsers.filter((pinnedUser) => pinnedUser.id !== recentUser.id)];
    setPinnedUsers(updatedPinnedUsers);
    localStorage.setItem(PINNED_USERS_STORAGE_KEY, JSON.stringify(updatedPinnedUsers));
  };

  const unpinUser = (userId: string) => {
    const updatedPinnedUsers = pinnedUsers.filter((pinnedUser) => pinnedUser.id !== userId);
    setPinnedUsers(updatedPinnedUsers);
    localStorage.setItem(PINNED_USERS_STORAGE_KEY, JSON.stringify(updatedPinnedUsers));
  };

  return (
    <Box>
      <Typography variant="h4">Pinned users</Typography>
      <Stack spacing={0.5} sx={{ mb: 2 }}>
        {user && (
          <Box>
            <Link href={`/users/${user.id_member}`}>{user.email}</Link> &nbsp;
            {user.tenant && <TenantBrandChip tenantValue={user.tenant} />} (you)
          </Box>
        )}
        {isProduction &&
          PROD_TEST_USERS.map((testUser) => (
            <Box key={testUser.id}>
              <Link href={`/users/${testUser.id}`}>{testUser.name}</Link>
            </Box>
          ))}
        {pinnedUsers.map((pinnedUser) => (
          <Box key={pinnedUser.id}>
            <Tooltip title="Unpin user">
              <IconButton
                aria-label={`Unpin ${pinnedUser.label}`}
                color="primary"
                size="small"
                onClick={() => unpinUser(pinnedUser.id)}
                sx={{ mr: 0.5 }}
              >
                <PushPinIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Link href={`/users/${pinnedUser.id}`}>user</Link> &nbsp;{' '}
            <Link href={`/purchases/section/allOrders?customer_id=${pinnedUser.id}`}>orders</Link> &nbsp;{' '}
            <Link href={`/users/${pinnedUser.id}/history`}>history</Link> &nbsp;
            <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]}>
              <Button size="small" onClick={() => setSpoofUser({ id: pinnedUser.id, label: pinnedUser.label })}>
                spoof
              </Button>
            </PermissionedComponent>
            {pinnedUser.label} &nbsp;
            {pinnedUser.tenant && <TenantBrandChip tenantValue={pinnedUser.tenant} />} &nbsp;
          </Box>
        ))}
      </Stack>
      <Typography variant="h4">Recently viewed users</Typography>
      {unpinnedRecentUsers.map((user, idx) => (
        <Box key={user.id}>
          {isNewDay(idx, user.date, unpinnedRecentUsers) && (
            <Typography component="pre" variant="body2" sx={{ marginTop: 2, fontFamily: 'monospace' }}>
              {formatDateWeekDayShortMonth(user.date)}
            </Typography>
          )}
          <Tooltip title="Pin user">
            <IconButton aria-label={`Pin ${user.label}`} size="small" onClick={() => pinUser(user)} sx={{ mr: 0.5 }}>
              <PushPinOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Link href={`/users/${user.id}`}>user</Link> &nbsp;{' '}
          <Link href={`/purchases/section/allOrders?customer_id=${user.id}`}>orders</Link> &nbsp;{' '}
          <Link href={`/users/${user.id}/history`}>history</Link> &nbsp;
          <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]}>
            <Button size="small" onClick={() => setSpoofUser({ id: user.id, label: user.label })}>
              spoof
            </Button>
          </PermissionedComponent>
          {user.label} &nbsp;
          {user.tenant && <TenantBrandChip tenantValue={user.tenant} />} &nbsp;
        </Box>
      ))}
      {spoofUser && (
        <SpoofUserDialog
          open={Boolean(spoofUser)}
          onClose={() => setSpoofUser(null)}
          userId={spoofUser.id}
          userLabel={spoofUser.label}
          orderUuid={spoofUser.orderUuid}
        />
      )}
    </Box>
  );
}
